import type { IContainer } from '@monofe/di';
import { buildBindable } from '@monofe/di';
import {
    injectConfigAPI,
    injectFetch,
    injectLog,
    injectProgressPromise,
    TYPES as UTIL_TYPES,
} from '@monofe/util';
import type { MediaBinaryStorageTypeMap } from '../../controller/interface/media';
import { bindMediaAPI, injectMediaAPI, TYPES, unbindMediaAPI } from '../interface/bind';
import type { IMediaAPI, IMediaURLResolverParameters } from '../interface/media';
import mediaImpl from './mediaVTS';

function _build(container: IContainer): Readonly<IMediaAPI> {
    const promiser = injectProgressPromise(container);
    const fetcher = injectFetch(container);
    const log = injectLog(container);
    const config = injectConfigAPI(container);

    const module: IMediaAPI = {
        getMedia: <
            T extends keyof MediaBinaryStorageTypeMap,
            U extends MediaBinaryStorageTypeMap[T],
        >(
            type: T,
            mediaId: string,
            parameters?: IMediaURLResolverParameters,
        ) =>
            mediaImpl.getMedia<T, U>(
                promiser,
                config,
                fetcher,
                log,
                type,
                mediaId,
                parameters ?? {},
            ),

        /* IMediaURLResolver */
        canResolveMedium: (mediaId: string) => mediaImpl.canResolveMedium(mediaId),

        requestURL: (mediaId: string, parameters: IMediaURLResolverParameters = {}) =>
            mediaImpl.requestURL(config, mediaId, parameters),
    };

    return Object.freeze(module);
}

export default buildBindable<IMediaAPI>({
    id: TYPES.IMediaAPI,
    dependencies: [UTIL_TYPES.IProgressPromiseGenerator, UTIL_TYPES.IFetch, UTIL_TYPES.IConfigAPI],
    bind: (value, container) => {
        bindMediaAPI([value], container);
    },
    remove: unbindMediaAPI,
    build: container => Promise.resolve(_build(container)),
    inject: container => {
        const [mediaAPI] = injectMediaAPI(container);
        if (mediaAPI === undefined) throw new Error("Couldn't find media API after injection");
        return mediaAPI;
    },
});
